import {storageHelper} from "@core-ui/utility";
import {useEffect, useRef} from "react";
import type {
  StorageEffectType,
  StorageJSONAdapterType,
  StorageStringAdapterType,
  UseStorageType,
  UseStorageOptionType,
} from "./types";

export function createStorageJSONAdapter<T extends Record<string, unknown>>(
  storageKey: string,
  storage: Storage | null
): StorageJSONAdapterType<T> | never {
  const setData = storage
    ? (data: T): void | never => {
        try {
          if (typeof data !== "object") {
            JSON.parse(data);
          }
        } catch (e) {
          throw new Error("setData: input is not a valid JSON.");
        }
        storage.setItem(storageKey, JSON.stringify(data));
      }
    : (data: T): void | never => {};

  const getData = storage
    ? (): T | never => {
        try {
          const storedData = JSON.parse(storage.getItem(storageKey) || "{}");
          return storedData;
        } catch (e) {
          throw new Error(
            "getData: can not parse stored data into a valid JSON."
          );
        }
      }
    : (): T | never => {
        return {} as T;
      };

  const update = storage
    ? (data: Partial<T>): void | never => {
        try {
          if (typeof data !== "object") {
            JSON.parse(data);
          }
        } catch (e) {
          throw new Error("update: input is not a valid JSON.");
        }

        const storedData = getData();
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            ...storedData,
            ...data,
          })
        );
      }
    : (data: Partial<T>): void | never => {};

  const set = storage
    ? <P extends keyof T>(key: P, value: T[P]): void | never => {
        if (!key) {
          throw new Error("useStorage: A key must be provided");
        }
        if (value === undefined) {
          throw new Error("useStorage: A value must be provided");
        }

        const data = getData();
        data[key] = value;
        setData(data);
      }
    : <P extends keyof T>(key: P, value: T[P]): void | never => {};

  const get = storage
    ? <P extends keyof T>(key: P): T[P] | undefined | never => getData()?.[key]
    : <P extends keyof T>(key: P): T[P] | undefined | never => undefined;

  const clear = storage
    ? (): void | never => {
        storage.removeItem(storageKey);
      }
    : (): void | never => {};

  return {
    key: storageKey,
    setData,
    getData,
    update,
    set,
    get,
    clear,
  };
}

export function createStorageStringAdapter(
  storageKey: string,
  storage: Storage | null
): StorageStringAdapterType | never {
  const setData = storage
    ? (data: string): void | never => {
        storage.setItem(storageKey, data);
      }
    : (data: string): void | never => {};

  const getData = storage
    ? (): string | never => {
        const data = storage.getItem(storageKey) || "";
        return data;
      }
    : (): string | never => "";

  const clear = storage
    ? (): void | never => {
        storage.removeItem(storageKey);
      }
    : (): void | never => {};

  return {
    key: storageKey,
    setData,
    getData,
    clear,
  };
}

// overload
export function useStorage<T extends Record<string, unknown>>(
  storageKey: string,
  options?: Partial<UseStorageOptionType> & {
    jsonify?: true; // when jsonify option is not there consider the value set to true!
  }
): StorageJSONAdapterType<T>;

export function useStorage(
  storageKey: string,
  options?: Partial<UseStorageOptionType> & {
    jsonify: false;
  }
): StorageStringAdapterType;

export function useStorage<T extends Record<string, unknown>>(
  storageKey: string,
  options: Partial<UseStorageOptionType> = {
    jsonify: true, // default is true when set to false, will consider value as string.
  }
): UseStorageType<T> {
  let jsonify;
  let globalScope;
  let storage = null;

  try {
    ({jsonify = true, globalScope = window} = options);
  } catch (e) {
    // SSR
    jsonify = true;
    globalScope = null;
  }
  // set up local storage
  let localStorage: Storage;
  if (globalScope && globalScope.localStorage) {
    localStorage = globalScope.localStorage;
    storage = storageHelper(localStorage);
  }

  return jsonify
    ? (createStorageJSONAdapter(
        storageKey,
        storage
      ) as StorageJSONAdapterType<T>)
    : (createStorageStringAdapter(
        storageKey,
        storage
      ) as StorageStringAdapterType);
}

export const useStorageEffect = (
  effect: StorageEffectType,
  deps: Array<string>,
  globalScope?: Window
): void => {
  let globalThis: Window | undefined;
  if (globalScope) {
    globalThis = globalScope;
  } else if (typeof window !== "undefined") {
    globalThis = window;
  }
  const savedEffect = useRef<(e: StorageEvent) => void>();
  useEffect(() => {
    savedEffect.current = (e: StorageEvent) => {
      if (deps.length === 0 || (e.key && deps.includes(e.key))) {
        effect(e.key, e.oldValue, e.newValue);
      }
    };
  }, [effect]);
  useEffect(() => {
    const listener = (event: StorageEvent) =>
      savedEffect.current && savedEffect.current(event);
    if (typeof globalThis !== "undefined" && "onstorage" in globalThis) {
      // check if onstorage event is supported
      globalThis.addEventListener("storage", listener);
    }

    return () => {
      if (typeof globalThis !== "undefined" && "onstorage" in globalThis) {
        globalThis.removeEventListener("storage", listener);
      }
    };
  }, [...deps, globalThis]);
};
